import { Suspense } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { XIcon, Bug, ServerCrash, KeyRound, ShieldCheck, Sprout } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useDevPanelStore } from '@/stores/devPanelStore';
import { ErrorTriggers } from './ErrorTriggers';
import { SeedFromExport } from './SeedFromExport';
import { AuthInspector } from './AuthInspector';
import { CapabilityOverrides } from './CapabilityOverrides';
import { EmbedPreview } from './EmbedPreview';
import { EnvFooter } from './EnvFooter';

const tabContentClass = 'min-h-0 flex-1 overflow-y-auto px-5 py-4';

function TabFallback() {
  return <div className="px-5 py-4 text-xs text-muted-foreground">Loading…</div>;
}

export function GlobalDevPanel() {
  const isOpen = useDevPanelStore((s) => s.isOpen);
  const close = useDevPanelStore((s) => s.close);

  return (
    <DialogPrimitive.Root open={isOpen} onOpenChange={(open) => !open && close()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[200] bg-black/40 backdrop-blur-[2px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0" />
        <DialogPrimitive.Content className="fixed right-0 top-0 z-[201] flex h-full w-[460px] max-w-[92vw] flex-col border-l border-border/60 bg-background shadow-xl shadow-black/[0.08] outline-none duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right">
          <div className="flex items-start justify-between border-b border-border/50 px-5 py-4">
            <div className="flex flex-col gap-1">
              <DialogPrimitive.Title className="flex items-center gap-2 text-base font-semibold">
                <Bug size={16} className="text-primary" />
                Dev Panel
              </DialogPrimitive.Title>
              <DialogPrimitive.Description className="text-xs text-muted-foreground">
                Inspect auth, override capabilities, exercise the error surface and rebuild a
                reported transaction from its debug export. Admin only.
              </DialogPrimitive.Description>
            </div>
            <DialogPrimitive.Close className="rounded-md p-1 text-muted-foreground opacity-70 transition-opacity hover:opacity-100">
              <XIcon size={16} />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          </div>

          <Tabs defaultValue="errors" className="flex min-h-0 flex-1 flex-col">
            <TabsList className="mx-5 mt-4 grid w-auto grid-cols-4">
              <TabsTrigger value="errors" className="gap-1.5">
                <ServerCrash size={14} /> Errors
              </TabsTrigger>
              <TabsTrigger value="auth" className="gap-1.5">
                <KeyRound size={14} /> Auth
              </TabsTrigger>
              <TabsTrigger value="access" className="gap-1.5">
                <ShieldCheck size={14} /> Access
              </TabsTrigger>
              <TabsTrigger value="seed" className="gap-1.5">
                <Sprout size={14} /> Seed
              </TabsTrigger>
            </TabsList>

            <TabsContent value="errors" className={tabContentClass}>
              <ErrorTriggers />
            </TabsContent>

            <TabsContent value="auth" className={tabContentClass}>
              <Suspense fallback={<TabFallback />}>
                <AuthInspector />
              </Suspense>
            </TabsContent>

            <TabsContent value="access" className={tabContentClass}>
              {import.meta.env.DEV && (
                <div className="mb-4">
                  <EmbedPreview />
                </div>
              )}
              <Suspense fallback={<TabFallback />}>
                <CapabilityOverrides />
              </Suspense>
            </TabsContent>

            <TabsContent value="seed" className={tabContentClass}>
              <SeedFromExport />
            </TabsContent>
          </Tabs>

          <EnvFooter />
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
